import type {ReactElement} from 'react';
import React, {useEffect, useRef, useState} from 'react';
import type {
  AiPromptBoxContextualMenuProps,
  ContextualMenuGroup,
  ContextualMenuItem,
  ContextualMenuItemWithEmoji,
  ContextualMenuItemWithIcon,
  ContextualMenuItemWithImage,
} from './ai-prompt-box-contextual-menu.types';
import {ContextualMenuGroupType, ContextualMenuItemGraphicType} from './ai-prompt-box-contextual-menu.types';
import {Text, TextSize} from '@Components/text';
import {PillButton} from '@Components/pill-button';
import {PillButtonSize} from '@Components/pill-button/pill-button.types';
import {LabelledCheckbox} from '@Components/labelled-checkbox';
import styles from './ai-prompt-box-contextual-menu.module.scss';
import {Icon} from '@Components/icon-v2';
import {IconShape, IconSize, IconType} from '@Components/icon-v2/icon.types';
import {Pill, PILL_BACKGROUND_COLOR, PILL_CONTENT_TYPE, PILL_SIZE} from '@Components/pill';
import {useAiPromptBox} from '@Components/ai-prompt-box/hooks/useAiPromptBox';
import {Dropdown} from '@Components/dropdown-v2';
import {ALIGNMENT_TYPE, DROPDOWN_POSITION} from '@Components/dropdown-v2/dropdown.types';
import {AiPromptBoxSize} from '@Components/ai-prompt-box/ai-prompt-box.types';
import useDefaultPanelClose from '@Panels/hooks/useDefaultPanelClose';

const contextualMenuPanelId = 'ai-contextual-menu-preferences';

export function AiPromptBoxContextualMenu(props: AiPromptBoxContextualMenuProps): ReactElement | null {
  const {state, actions} = useAiPromptBox(props.promptBoxId);
  const contextualMenuGroups = state.contextualMenu ?? [];
  const contextualMenuDropdownRef = useRef<HTMLDivElement>(null);
  const [contextualMenuWidth, setContextualMenuWidth] = useState<number>(0);
  const [showContextualMenuDropdown, setShowContextualMenuDropdown] = useState(false);
  const CONTEXTUAL_MENU_POPUP_OFFSET = 4;
  const closeOptionsModal = useDefaultPanelClose(contextualMenuPanelId);

  const iconSizeClassMap = {
    [AiPromptBoxSize.SMALL]: IconSize.SIZE_ICON_16,
    [AiPromptBoxSize.MEDIUM]: IconSize.SIZE_ICON_20,
    [AiPromptBoxSize.SINGLE_LINE]: IconSize.SIZE_ICON_16,
    [AiPromptBoxSize.SMALL_EXTENDED]: IconSize.SIZE_ICON_16,
  };

  useEffect(() => {
    document.addEventListener('touchstart', closeContextualMenuDropdownListener);
    document.addEventListener('mousedown', closeContextualMenuDropdownListener);

    return (): void => {
      document.removeEventListener('touchstart', closeContextualMenuDropdownListener);
      document.removeEventListener('mousedown', closeContextualMenuDropdownListener);
    };
  }, []);

  const updateContextualMenuWidth = (): void => {
    const maxContextualMenuWidth = props.maxContextualMenuWidth ?? 280;
    if (props.footerRef.current && !contextualMenuWidth) {
      const width = Math.min(props.footerRef.current.offsetWidth, maxContextualMenuWidth);
      setContextualMenuWidth(width);
    }
  };

  const closeContextualMenuDropdownListener = (e: MouseEvent | TouchEvent): void => {
    const target = e.target as HTMLElement;

    if (!contextualMenuDropdownRef.current?.contains(target)) {
      setShowContextualMenuDropdown(false);
    }
  };

  const handleItemClick = (groupIndex: number, itemIndex: number): void => {
    const group = contextualMenuGroups[groupIndex];
    const updatedGroups = [...contextualMenuGroups];

    switch (group.type) {
      case ContextualMenuGroupType.CARDS:
      case ContextualMenuGroupType.PILLS:
        updatedGroups[groupIndex] = {
          ...group,
          items: group.items.map((item, idx) => ({
            ...item,
            active: idx === itemIndex,
          })),
        };
        break;
      case ContextualMenuGroupType.CHECKBOXES:
        updatedGroups[groupIndex] = {
          ...group,
          items: group.items.map((item, idx) => ({
            ...item,
            active: idx === itemIndex ? !item.active : item.active,
          })),
        };
        break;
      default:
        return;
    }

    closeOptionsModal();
    actions.update({contextualMenu: updatedGroups});
  };

  const getGroupHeading = (group: ContextualMenuGroup): ReactElement => {
    return <Text bold val={group.label} size={TextSize.XXSMALL} className={styles.contextualMenuHeading} />;
  };

  const getItemGraphic = (item: ContextualMenuItem | ContextualMenuItemWithEmoji | ContextualMenuItemWithIcon | ContextualMenuItemWithImage): ReactElement | null => {
    if (!('graphicType' in item)) {
      return null;
    }

    switch (item.graphicType) {
      case ContextualMenuItemGraphicType.EMOJI:
        return <span className={styles.itemEmoji}>{item.emoji}</span>;
      case ContextualMenuItemGraphicType.ICON:
        return <Icon icon={item.icon} size={IconSize.SIZE_ICON_16} type={item.active ? IconType.PRIMARY : IconType.SECONDARY} />;
      case ContextualMenuItemGraphicType.IMAGE:
        return <img src={item.imageUrl} alt={item.label} className={styles.itemImage} />;
    }
  };

  const getPillButton = (item: ContextualMenuItem, groupIndex: number, itemIndex: number): ReactElement => {
    const leftIcon =
      'graphicType' in item && item.graphicType === ContextualMenuItemGraphicType.ICON ? {icon: (item as ContextualMenuItemWithIcon).icon, size: IconSize.SIZE_ICON_16} : undefined;
    const emoji = 'graphicType' in item && item.graphicType === ContextualMenuItemGraphicType.EMOJI ? (item as ContextualMenuItemWithEmoji).emoji : undefined;
    const displayText = emoji ? `${emoji} ${item.label}` : item.label;

    return (
      <PillButton
        key={item.value}
        text={displayText}
        isSelected={item.active && !item.disabled}
        size={PillButtonSize.SMALL}
        leftIconProps={leftIcon}
        isDisabled={item.disabled}
        onClick={() => {
          handleItemClick(groupIndex, itemIndex);
        }}
        textSize={TextSize.XXSMALL}
      />
    );
  };

  function buildSelectorClassNameForSize(): string {
    if (state.size === AiPromptBoxSize.MEDIUM) {
      return styles.mediumContextualMenuIcon;
    }
    return styles.smallContextualMenuIcon;
  }

  function buildCardClassName(isActive: boolean, isDisabled: boolean): string {
    if (isDisabled) {
      return styles.card + ' ' + styles.disabled;
    }

    if (isActive) {
      return styles.card + ' ' + styles.selected;
    }

    return styles.card;
  }

  function buildLabelClassName(isActive: boolean, isDisabled: boolean): string {
    if (isDisabled) {
      return 'content-disabled-dark';
    }

    if (isActive) {
      return 'content-primary';
    }

    return '';
  }

  function buildHybridPillClassName(isActive: boolean): string {
    if (isActive) {
      return `${styles.pill} bg-primary-20`;
    }

    return `${styles.pill} bg-ai-light`;
  }

  function buildIconPillClassName(isActive: boolean): string {
    if (isActive) {
      return 'spacing-p-l-2 spacing-p-r-2 spacing-p-t-1 spacing-p-b-1 bg-primary-20 content-primary';
    }

    return 'spacing-p-l-2 spacing-p-r-2 spacing-p-t-1 spacing-p-b-1 bg-ai-light content-secondary';
  }

  function renderRightPill(item: ContextualMenuItem): ReactElement | null {
    const isActive = item.active;
    const isDisabled = item.disabled;
    const hasCredits = item.credits !== undefined && item.topRightIcon;

    if (hasCredits) {
      return (
        <Pill
          content={{
            type: PILL_CONTENT_TYPE.HYBRID,
            icon: item.topRightIcon ?? '',
            text: (item.credits ?? 0).toString(),
            isBold: true,
            textColor: isDisabled ? 'content-disabled' : isActive ? 'content-primary' : 'content-secondary',
            textSize: TextSize.XXSMALL,
            iconSize: IconSize.SIZE_ICON_12,
            iconClasses: isDisabled ? 'content-disabled' : isActive ? 'content-primary' : 'content-ai',
            gap: 1,
          }}
          size={PILL_SIZE.XSMALL}
          background={PILL_BACKGROUND_COLOR.NONE}
          isFlat={true}
          isDisabled={item.disabled}
          className={buildHybridPillClassName(isActive)}
        />
      );
    }

    if (item.topRightIcon) {
      return (
        <Pill
          content={{
            type: PILL_CONTENT_TYPE.ICON,
            icon: item.topRightIcon,
            size: IconSize.SIZE_ICON_12,
          }}
          size={PILL_SIZE.XSMALL}
          background={PILL_BACKGROUND_COLOR.NONE}
          isFlat={true}
          className={buildIconPillClassName(isActive)}
        />
      );
    }

    return null;
  }

  function GraphicBlock(props: {shouldRender: boolean; item: ContextualMenuItem}): ReactElement | null {
    if (!props.shouldRender) {
      return null;
    }

    return getItemGraphic(props.item);
  }

  function DescriptionBlock(props: {description: string | undefined; isActive: boolean; isDisabled?: boolean}): ReactElement | null {
    if (!props.description) {
      return null;
    }

    return <Text val={props.description} size={TextSize.XXSMALL} className={props.isDisabled ? 'content-disabled' : props.isActive ? 'content-primary' : ''} />;
  }

  const getCard = (item: ContextualMenuItem, groupIndex: number, itemIndex: number): ReactElement => {
    const isActive = item.active;
    const isDisabled = item.disabled ?? false;
    const hasGraphic = 'graphicType' in item && item.graphicType !== ContextualMenuItemGraphicType.NONE;

    const onClick = (isActive: boolean, isDisabled: boolean): void => {
      if (!isActive && !isDisabled) {
        handleItemClick(groupIndex, itemIndex);
      }
    };

    const cardClassName = buildCardClassName(isActive, isDisabled);
    const labelClassName = buildLabelClassName(isActive, isDisabled);

    return (
      <div key={item.value} className={cardClassName} onClick={onClick.bind(null, isActive, isDisabled)}>
        <GraphicBlock shouldRender={hasGraphic} item={item} />

        <div className="flex-row-align-center flex-justify-between">
          <div className="flexbox gap-2 flex-align-items-center">
            <Text bold={true} val={item.label} size={TextSize.XXSMALL} className={labelClassName} />
          </div>

          {renderRightPill(item)}
        </div>

        <DescriptionBlock description={item.description} isActive={isActive} isDisabled={isDisabled} />
      </div>
    );
  };

  const getCheckbox = (item: ContextualMenuItem, groupIndex: number, itemIndex: number): ReactElement => {
    const graphic = 'graphicType' in item && item.graphicType === ContextualMenuItemGraphicType.ICON ? (item as ContextualMenuItemWithIcon).icon : undefined;

    return (
      <div key={item.value}>
        <LabelledCheckbox
          isChecked={item.active}
          label={item.label}
          labelIcon={graphic}
          onChange={() => {
            handleItemClick(groupIndex, itemIndex);
          }}
          textClassName={styles.checkboxLabel}
          labelIconClassName={styles.checkboxIcon}
          textSize={TextSize.XXSMALL}
        />
      </div>
    );
  };

  const getGroupItems = (group: ContextualMenuGroup, groupIndex: number): ReactElement => {
    switch (group.type) {
      case ContextualMenuGroupType.PILLS:
        return <div className={styles.contextualMenuPillContent}>{group.items.map((item, itemIndex) => getPillButton(item, groupIndex, itemIndex))}</div>;
      case ContextualMenuGroupType.CARDS:
        return <div className={'flexbox flex-column gap-2'}>{group.items.map((item, itemIndex) => getCard(item, groupIndex, itemIndex))}</div>;
      case ContextualMenuGroupType.CHECKBOXES:
        return <div className={styles.contextualMenuContainer}>{group.items.map((item, itemIndex) => getCheckbox(item, groupIndex, itemIndex))}</div>;
    }
  };

  const getGroup = (group: ContextualMenuGroup, groupIndex: number): ReactElement => {
    return (
      <div key={groupIndex} className={'flexbox flex-column gap-2'}>
        {getGroupHeading(group)}
        {getGroupItems(group, groupIndex)}
      </div>
    );
  };

  const getContextualMenuContent = (): ReactElement => {
    return (
      <div
        style={{maxWidth: contextualMenuWidth > 0 ? contextualMenuWidth : '100%'}}
        className={'flexbox flex-column gap-4 spacing-p-t-3 spacing-p-r-4 spacing-p-b-5 spacing-p-l-4 _cursor-default'}
      >
        {contextualMenuGroups.map((group, index) => getGroup(group, index))}
      </div>
    );
  };

  const getContextualMenuContentForMobile = (): ReactElement => {
    return <div className={'flexbox flex-column gap-4 spacing-p-r-4 spacing-p-l-4 spacing-p-b-4'}>{contextualMenuGroups.map((group, index) => getGroup(group, index))}</div>;
  };

  if (!contextualMenuGroups) {
    return null;
  }

  return (
    <div ref={contextualMenuDropdownRef}>
      <Dropdown
        open={showContextualMenuDropdown}
        onOpen={() => {
          updateContextualMenuWidth();
          props.onOpen?.();
          setShowContextualMenuDropdown(true);
        }}
        onClose={() => {
          setShowContextualMenuDropdown(false);
        }}
        doesCloseOnOuterClick
        doCloseOnInnerClick={false}
        selector={
          <Icon
            icon={'icon-filter-1'}
            size={iconSizeClassMap[state.size]}
            shape={IconShape.CIRCLE}
            className={`spacing-p-2 ${styles.contextualMenuDropdownSelector} ${buildSelectorClassNameForSize()}`}
          />
        }
        selectorClassNameOnOpen={styles.contextualMenuDropdownSelectorOpen}
        popup={getContextualMenuContent()}
        popUpHasCustomWidth
        popupClassName={styles.contextualMenuPopup}
        alignment={ALIGNMENT_TYPE.BOTTOM_START}
        shouldShift={true}
        shouldFlip={true}
        leftOffset={-10}
        topOffset={12}
        popupOffset={CONTEXTUAL_MENU_POPUP_OFFSET}
        showModalForSmallScreen
        position={DROPDOWN_POSITION.FIXED}
        modalTitle={window.i18next.t('pmwjs_preferences')}
        modalClassName={styles.contextualMenuModal}
        panelIdForModal={contextualMenuPanelId}
        modalContent={getContextualMenuContentForMobile()}
      />
    </div>
  );
}
